import { useMemo, useState } from "react"
import { toast } from "react-toastify"
import { useCompanyControllerFindAllCompaniesQuery } from "~/ui-rtk/api/companyApi"
import {
  AdminJobScheduleView,
  useCreateAdminJobScheduleMutation,
  useDeleteAdminJobScheduleMutation,
  useListAdminJobSchedulesQuery,
  useListJobTypesQuery,
  useRunAdminJobScheduleNowMutation,
  useUpdateAdminJobScheduleMutation,
} from "~/ui-rtk/api/jobsApi"
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import { Button, Input, Select, Toggle } from "~/ui-rtk/components/ui/controls"
import MultiSelectWithChips from "~/ui-rtk/components/ui/controls/MultiSelectWithChips/MultiSelectWithChips"
import type { TMultiSelectOption } from "~/ui-rtk/components/ui/controls/MultiSelect"
import { getJobLabel } from "./jobViews"

/** Absolute short local datetime, e.g. "Jul 10, 9:00 AM". */
function formatDateTime(iso?: string | null): string {
  if (!iso) return "—"
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return "—"
  return d.toLocaleString([], {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  })
}

/** Shared modal chrome — the Dialog component itself paints no background. */
const DIALOG_CLASSES = {
  container:
    "bg-basic-black border border-system-blue-600/40 p-6 rounded-xl text-left",
}

function prettyJson(value: unknown): string {
  if (value === undefined || value === null) return "{}"
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return "{}"
  }
}

/** Shared cron/timezone/input fields of the create + edit dialogs. */
type ScheduleFormState = {
  cron: string
  timezone: string
  inputJson: string
}

function ScheduleFormFields({
  form,
  onChange,
}: {
  form: ScheduleFormState
  onChange: (next: ScheduleFormState) => void
}) {
  // The input JSON is the single source of truth; the Slack channel field is
  // a convenience mirror of its `slackChannelId` key (jobs that post reports —
  // ad-blast-report, meta-ads-qa — read it; blank = the default channel).
  const parsedInput = useMemo<Record<string, unknown> | null>(() => {
    try {
      const parsed = form.inputJson.trim() ? JSON.parse(form.inputJson) : {}
      return typeof parsed === "object" &&
        parsed !== null &&
        !Array.isArray(parsed)
        ? (parsed as Record<string, unknown>)
        : null
    } catch {
      return null
    }
  }, [form.inputJson])
  const slackChannelId =
    typeof parsedInput?.slackChannelId === "string"
      ? parsedInput.slackChannelId
      : ""

  const handleSlackChannelChange = (value: string) => {
    if (!parsedInput) return // JSON mid-edit / invalid — don't clobber it
    const next = { ...parsedInput }
    if (value.trim()) {
      next.slackChannelId = value.trim()
    } else {
      delete next.slackChannelId
    }
    onChange({ ...form, inputJson: JSON.stringify(next, null, 2) })
  }

  return (
    <>
      <div className="flex gap-3">
        <label className="flex-1">
          <span className="mb-1 block text-xs text-system-gray-400">
            Cron (5-field, e.g. <code>0 7 * * 1</code>)
          </span>
          <Input
            value={form.cron}
            fullWidth
            onChange={e => onChange({ ...form, cron: e.target.value })}
            placeholder="0 7 * * 1"
          />
        </label>
        <label className="flex-1">
          <span className="mb-1 block text-xs text-system-gray-400">
            Timezone (IANA, blank = UTC)
          </span>
          <Input
            value={form.timezone}
            fullWidth
            onChange={e => onChange({ ...form, timezone: e.target.value })}
            placeholder="UTC"
          />
        </label>
      </div>
      <label className="block">
        <span className="mb-1 block text-xs text-system-gray-400">
          Slack channel ID (optional — for jobs that post reports)
        </span>
        <Input
          value={slackChannelId}
          fullWidth
          disabled={!parsedInput}
          onChange={e => handleSlackChannelChange(e.target.value)}
          placeholder="e.g. C0AGENCY123 (blank = default channel)"
        />
        <span className="mt-1 block text-[11px] text-system-gray-500">
          Mirrors <code>slackChannelId</code> in the input payload below. The
          bot must be a member of the channel.
        </span>
      </label>
      <label className="block">
        <span className="mb-1 block text-xs text-system-gray-400">
          Input payload (JSON, passed to every run)
        </span>
        <textarea
          className="textarea textarea-bordered h-48 w-full font-mono text-xs"
          value={form.inputJson}
          onChange={e => onChange({ ...form, inputJson: e.target.value })}
          spellCheck={false}
        />
      </label>
    </>
  )
}

/** Parse the JSON textarea; toast + return undefined when invalid. */
function parseInputJson(inputJson: string): unknown | undefined {
  try {
    return inputJson.trim() === "" ? {} : JSON.parse(inputJson)
  } catch (e) {
    toast.error(`Input is not valid JSON: ${(e as Error).message}`)
    return undefined
  }
}

function EditScheduleDialog({
  schedule,
  onClose,
}: {
  schedule: AdminJobScheduleView
  onClose: () => void
}) {
  const [form, setForm] = useState<ScheduleFormState>({
    cron: schedule.cron,
    timezone: schedule.timezone ?? "",
    inputJson: prettyJson(schedule.input),
  })
  const [updateSchedule, { isLoading }] = useUpdateAdminJobScheduleMutation()

  const handleSave = async () => {
    const input = parseInputJson(form.inputJson)
    if (input === undefined) return
    try {
      await updateSchedule({
        id: schedule.id,
        cron: form.cron,
        timezone: form.timezone.trim() || undefined,
        input,
      }).unwrap()
      toast.success("Schedule updated")
      onClose()
    } catch (e) {
      const message = (e as { data?: { message?: string } })?.data?.message
      toast.error(message ?? "Couldn't update the schedule")
    }
  }

  return (
    <Dialog isOpen handleClose={onClose} width={640} classes={DIALOG_CLASSES}>
      <div className="flex flex-col gap-4">
        <div>
          <h2 className="text-lg font-bold text-white">
            Edit schedule — {getJobLabel(schedule.type)}
          </h2>
          <p className="text-xs text-system-gray-400">
            {schedule.companyName} · <code>{schedule.type}</code>
          </p>
        </div>
        <ScheduleFormFields form={form} onChange={setForm} />
        <div className="flex justify-end gap-2 border-t border-system-blue-600/30 pt-4">
          <Button
            variant={{ variant: "text" }}
            size="sm"
            type="button"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant={{ variant: "solid", color: "pink" }}
            size="sm"
            type="button"
            isLoading={isLoading}
            disabled={isLoading || !form.cron.trim()}
            onClick={handleSave}
          >
            Save
          </Button>
        </div>
      </div>
    </Dialog>
  )
}

function CreateScheduleDialog({ onClose }: { onClose: () => void }) {
  const { data: companies } = useCompanyControllerFindAllCompaniesQuery()
  const { data: types } = useListJobTypesQuery()
  const [selectedCompanies, setSelectedCompanies] = useState<
    TMultiSelectOption[]
  >([])
  const [type, setType] = useState("")
  const [form, setForm] = useState<ScheduleFormState>({
    cron: "0 7 * * 1",
    timezone: "",
    inputJson: "{}",
  })
  const [createSchedule, { isLoading }] = useCreateAdminJobScheduleMutation()

  const companyOptions = useMemo<TMultiSelectOption[]>(
    () =>
      [...(companies ?? [])]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(c => ({ label: c.name, value: c.id })),
    [companies],
  )
  const typeOptions = useMemo(
    () =>
      (types ?? []).map(t => ({
        label: `${getJobLabel(t.type)} (${t.type})`,
        value: t.type,
      })),
    [types],
  )
  const selectedType = types?.find(t => t.type === type)

  /**
   * Selecting 2+ companies is the multi-company-job case (e.g.
   * ad-blast-report): mirror the whole selection into `input.companyIds` so
   * what gets submitted is always visible in the JSON editor. Dropping back
   * below 2 removes the key again. A JSON draft that doesn't parse is left
   * untouched — the user is mid-edit.
   */
  const handleCompaniesChange = (selected: TMultiSelectOption[]) => {
    setSelectedCompanies(selected)
    setForm(prev => {
      try {
        const parsed = prev.inputJson.trim()
          ? JSON.parse(prev.inputJson)
          : ({} as Record<string, unknown>)
        if (
          typeof parsed !== "object" ||
          parsed === null ||
          Array.isArray(parsed)
        ) {
          return prev
        }
        if (selected.length >= 2) {
          parsed.companyIds = selected.map(o => o.value)
        } else if (Array.isArray(parsed.companyIds)) {
          delete parsed.companyIds
        }
        return { ...prev, inputJson: JSON.stringify(parsed, null, 2) }
      } catch {
        return prev
      }
    })
  }

  const handleCreate = async () => {
    const input = parseInputJson(form.inputJson)
    if (input === undefined) return
    try {
      await createSchedule({
        type,
        // The first selected company owns the schedule row (its runs are
        // enqueued under it); additional selections ride in input.companyIds.
        companyId: selectedCompanies[0].value,
        cron: form.cron,
        timezone: form.timezone.trim() || undefined,
        input,
      }).unwrap()
      toast.success("Schedule created")
      onClose()
    } catch (e) {
      const message = (e as { data?: { message?: string } })?.data?.message
      toast.error(message ?? "Couldn't create the schedule")
    }
  }

  return (
    <Dialog isOpen handleClose={onClose} width={640} classes={DIALOG_CLASSES}>
      <div className="flex flex-col gap-4">
        <h2 className="text-lg font-bold text-white">New schedule</h2>
        <label className="block">
          <span className="mb-1 block text-xs text-system-gray-400">
            Job type
          </span>
          <Select
            className="w-full"
            options={typeOptions}
            placeholder="Select a job type…"
            value={type}
            onChange={e => setType(e.target.value)}
          />
        </label>
        {selectedType?.description && (
          <p className="text-xs text-system-gray-400">
            {selectedType.description}
          </p>
        )}
        <div>
          <span className="mb-1 block text-xs text-system-gray-400">
            Companies
          </span>
          <MultiSelectWithChips
            options={companyOptions}
            value={selectedCompanies}
            onChange={handleCompaniesChange}
            placeholder="Search companies…"
            showTypeAhead
          />
          <p className="mt-1 text-[11px] text-system-gray-500">
            The first company owns the schedule. Selecting more than one also
            writes <code>companyIds</code> into the input payload — for
            multi-company jobs like the ad blast report.
          </p>
        </div>
        <ScheduleFormFields form={form} onChange={setForm} />
        <div className="flex justify-end gap-2 border-t border-system-blue-600/30 pt-4">
          <Button
            variant={{ variant: "text" }}
            size="sm"
            type="button"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant={{ variant: "solid", color: "pink" }}
            size="sm"
            type="button"
            isLoading={isLoading}
            disabled={
              isLoading ||
              selectedCompanies.length === 0 ||
              !type ||
              !form.cron.trim()
            }
            onClick={handleCreate}
          >
            Create
          </Button>
        </div>
      </div>
    </Dialog>
  )
}

function ScheduleRow({
  schedule,
  onEdit,
}: {
  schedule: AdminJobScheduleView
  onEdit: (schedule: AdminJobScheduleView) => void
}) {
  const [updateSchedule] = useUpdateAdminJobScheduleMutation()
  const [deleteSchedule, { isLoading: isDeleting }] =
    useDeleteAdminJobScheduleMutation()
  const [runNow, { isLoading: isRunning }] = useRunAdminJobScheduleNowMutation()

  const handleToggle = async (enabled: boolean) => {
    try {
      await updateSchedule({ id: schedule.id, enabled }).unwrap()
      toast.success(enabled ? "Schedule enabled" : "Schedule disabled")
    } catch {
      toast.error("Couldn't update the schedule")
    }
  }

  const handleRunNow = async () => {
    try {
      const { jobRunId } = await runNow(schedule.id).unwrap()
      toast.success(`Run enqueued (job ${jobRunId.slice(0, 8)}…)`)
    } catch {
      toast.error("Couldn't run the schedule")
    }
  }

  const handleDelete = async () => {
    if (
      !window.confirm(
        `Delete the "${schedule.type}" schedule for ${schedule.companyName}? Future runs stop immediately.`,
      )
    ) {
      return
    }
    try {
      await deleteSchedule(schedule.id).unwrap()
      toast.success("Schedule deleted")
    } catch {
      toast.error("Couldn't delete the schedule")
    }
  }

  return (
    <tr className="border-b border-white/5 last:border-0 hover:bg-white/5">
      <td className="py-3 pr-3 align-top">
        <div className="text-sm font-medium text-system-gray-100">
          {schedule.companyName}
        </div>
        {schedule.createdBy && (
          <div className="text-[11px] text-system-gray-500">
            by {schedule.createdBy}
          </div>
        )}
      </td>
      <td className="py-3 pr-3 align-top">
        <div className="text-sm text-system-gray-100">
          {getJobLabel(schedule.type)}
        </div>
        <div className="font-mono text-[11px] text-system-gray-500">
          {schedule.type}
        </div>
      </td>
      <td className="py-3 pr-3 align-top">
        <code className="text-sm text-system-gray-300">{schedule.cron}</code>
        <div className="text-[11px] text-system-gray-500">
          {schedule.timezone ?? "UTC"}
        </div>
      </td>
      <td className="py-3 pr-3 align-top">
        <Toggle size="xs" value={schedule.enabled} onChange={handleToggle} />
      </td>
      <td className="whitespace-nowrap py-3 pr-3 align-top text-sm text-system-gray-300">
        {schedule.enabled ? formatDateTime(schedule.nextRunAt) : "—"}
      </td>
      <td className="whitespace-nowrap py-3 pr-3 align-top text-sm text-system-gray-500">
        {formatDateTime(schedule.lastRunAt)}
      </td>
      <td className="whitespace-nowrap py-3 align-top text-right">
        <button
          type="button"
          disabled={isRunning}
          onClick={handleRunNow}
          className="btn btn-ghost btn-xs text-sky-300 hover:bg-sky-500/10"
        >
          {isRunning ? "Running…" : "Run now"}
        </button>
        <button
          type="button"
          onClick={() => onEdit(schedule)}
          className="btn btn-ghost btn-xs text-system-gray-200 hover:bg-white/10"
        >
          Edit
        </button>
        <button
          type="button"
          disabled={isDeleting}
          onClick={handleDelete}
          className="btn btn-ghost btn-xs text-rose-300 hover:bg-rose-500/10"
        >
          {isDeleting ? "Deleting…" : "Delete"}
        </button>
      </td>
    </tr>
  )
}

/**
 * Super-admin page: every company's recurring job schedules in one table —
 * enable/disable, edit cron/timezone/input, fire immediately, delete, and
 * create new schedules for any company. Route + sidebar entry are gated to
 * SUPER_ADMIN; the backing /jobs/admin/* endpoints enforce the same scope.
 */
export default function AdminSchedulesPage() {
  const [typeFilter, setTypeFilter] = useState("")
  const [editing, setEditing] = useState<AdminJobScheduleView | null>(null)
  const [isCreating, setIsCreating] = useState(false)

  const {
    data: schedules,
    isLoading,
    isError,
  } = useListAdminJobSchedulesQuery(
    typeFilter ? { type: typeFilter } : undefined,
  )
  const { data: types } = useListJobTypesQuery()

  const typeFilterOptions = useMemo(
    () => [
      { label: "All job types", value: "" },
      ...(types ?? []).map(t => ({ label: t.type, value: t.type })),
    ],
    [types],
  )

  return (
    <div className="mx-auto w-full max-w-6xl p-6">
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Scheduled Jobs</h1>
          <p className="text-sm text-system-gray-400">
            Admin-only: recurring job schedules across every company. Toggle,
            edit the cron and input payload, fire immediately, or delete.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select
            className="w-52"
            options={typeFilterOptions}
            value={typeFilter}
            onChange={e => setTypeFilter(e.target.value)}
          />
          <Button
            variant={{ variant: "solid", color: "pink" }}
            size="sm"
            type="button"
            onClick={() => setIsCreating(true)}
          >
            New schedule
          </Button>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-system-blue-800/40 p-2">
        {isLoading && (
          <p className="p-6 text-sm text-system-gray-400">Loading schedules…</p>
        )}
        {isError && (
          <p className="p-6 text-sm text-rose-300">
            Couldn&apos;t load schedules.
          </p>
        )}
        {schedules && schedules.length === 0 && (
          <p className="p-6 text-sm text-system-gray-400">
            No recurring schedules{typeFilter ? " for this job type" : ""} yet.
          </p>
        )}
        {schedules && schedules.length > 0 && (
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-white/10 text-[11px] uppercase tracking-wide text-system-gray-500">
                <th className="py-2 pr-3 font-medium">Company</th>
                <th className="py-2 pr-3 font-medium">Job</th>
                <th className="py-2 pr-3 font-medium">Cron</th>
                <th className="py-2 pr-3 font-medium">Enabled</th>
                <th className="py-2 pr-3 font-medium">Next run</th>
                <th className="py-2 pr-3 font-medium">Last run</th>
                <th
                  className="py-2 text-right font-medium"
                  aria-label="Actions"
                />
              </tr>
            </thead>
            <tbody>
              {schedules.map(schedule => (
                <ScheduleRow
                  key={schedule.id}
                  schedule={schedule}
                  onEdit={setEditing}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <EditScheduleDialog
          // Remount on schedule switch so the form re-seeds from the row.
          key={editing.id}
          schedule={editing}
          onClose={() => setEditing(null)}
        />
      )}
      {isCreating && (
        <CreateScheduleDialog onClose={() => setIsCreating(false)} />
      )}
    </div>
  )
}
